import { onMounted, onBeforeUnmount } from 'vue'

/**
 * Composable untuk handle back button pada modal.
 * Menggunakan HTML5 History API agar back button HP menutup modal
 * sebelum navigasi ke halaman sebelumnya.
 *
 * @param {Object} options
 * @param {Ref<boolean>} options.isVisible      - Reactive ref state modal
 * @param {Function}     options.onClose        - Fungsi untuk menutup modal
 * @param {string}       [options.stateKey]     - Key unik untuk modal ini (default: 'modal')
 * 
 * @returns {Object} { pushModalHistory, popModalHistory }
 * 
 * @example
 * const isModalVisible = ref(false)
 * const { pushModalHistory, popModalHistory } = useModalHistory({
 *   isVisible: isModalVisible,
 *   onClose: () => { isModalVisible.value = false },
 *   stateKey: 'transaction-detail'
 * })
 * 
 * function showModal() {
 *   isModalVisible.value = true
 *   pushModalHistory()
 * }
 * 
 * function closeModal() {
 *   isModalVisible.value = false
 *   popModalHistory()
 * }
 */
export function useModalHistory({ isVisible, onClose, stateKey = 'modal' }) {
    const STATE_KEY = stateKey

    /**
     * Push modal state ke history stack
     * Panggil saat modal DIBUKA
     */
    function pushModalHistory() {
        // Preserve Vue Router's internal state (current, position, back, forward, etc.)
        // to prevent corrupting Vue Router's navigation and URL construction
        const currentState = window.history.state || {}
        window.history.pushState(
            { ...currentState, [STATE_KEY]: true },
            ''
        )
    }

    /**
     * Pop modal state dari history stack
     * Panggil saat modal DITUTUP oleh UI (bukan back button)
     */
    function popModalHistory() {
        if (window.history.state?.[STATE_KEY]) {
            window.history.back()
        }
    }

    /**
     * Handle popstate event (back button ditekan)
     * State sudah berubah (modal entry sudah di-pop)
     */
    function handlePopState(event) {
        // Back button ditekan — state sudah berubah (modal entry sudah di-pop)
        if (isVisible.value && !event.state?.[STATE_KEY]) {
            onClose()
        }
    }

    onMounted(() => {
        window.addEventListener('popstate', handlePopState)
    })

    onBeforeUnmount(() => {
        window.removeEventListener('popstate', handlePopState)
    })

    return {
        pushModalHistory, // panggil saat modal DIBUKA
        popModalHistory,  // panggil saat modal DITUTUP oleh UI (bukan back button)
    }
}
